import { CODE_FILET, categorieDe, moduleEvenement } from './evenementsCategories';

/**
 * Classement PROPOSÉ des événements — lot 5.4 bis, refondu au lot 5.4 ter.
 *
 * ── CE QUE CE FICHIER PROPOSE MAINTENANT ──────────────────────────────────────
 * Au 5.4 bis il proposait, pour chaque événement, un module lié, une catégorie
 * métier, un symbole et une couleur : quatre réglages × 33 objets. Depuis que les
 * catégories sont le référentiel, il ne propose plus qu'UNE chose par événement —
 * SA CATÉGORIE — et, en amont, LA CRÉATION DES CATÉGORIES QUI MANQUENT.
 *
 * Ce second point n'est pas un supplément : sur les 33 événements réels de QLIO
 * 2026-2027, DEUX catégories nécessaires n'existent pas dans les huit d'origine
 * (« Conseil de département » et « Vœux & enquêtes »). Sans la possibilité de les
 * créer, huit de ces événements n'auraient pas de place juste et retomberaient en
 * « Autre » — c'est-à-dire nulle part.
 *
 * ── CE QUE CE FICHIER N'EST PAS ───────────────────────────────────────────────
 * Ce n'est pas un classement automatique. Rien de ce qui est calculé ici n'est
 * écrit sans un clic. La raison est simple : le seul indice disponible est le
 * LIBELLÉ LIBRE saisi par un utilisateur, et un libellé est bruité. « RIMA » ne
 * veut rien dire pour une expression régulière — il a fallu demander (c'est une
 * Réunion d'Information des Maîtres d'Apprentissage). Un classement appliqué en
 * silence serait juste la plupart du temps et FAUX SANS LE DIRE le reste.
 *
 * ── ORDRE DES RÈGLES : IL COMPTE ──────────────────────────────────────────────
 * Première règle qui s'applique, on s'arrête. « Soutenance Saé Stage S4 » doit
 * être une SOUTENANCE, pas un stage ; « RÉUNION VACATAIRE » doit partir vers les
 * enseignants, pas vers la règle générique « réunion ». Toute règle ajoutée doit
 * l'être en connaissance de cet ordre — et vérifiée sur le jeu réel.
 */

/**
 * Catégories que les règles savent viser, avec de quoi les CRÉER si elles
 * manquent. Les huit premières sont celles de l'amorçage ; les quatre suivantes
 * n'existent nulle part tant qu'un événement ne les réclame pas.
 *
 * Symboles et couleurs repris de la sidebar quand la catégorie a un module : la
 * continuité de la charte est le défaut, pas une consigne à appliquer à la main.
 */
export const CATEGORIES_PROPOSABLES = {
    soutenance: { libelle: 'Soutenance',           icone: 'fa-microphone-lines',    couleur: '#F97316', module: 'soutenance' },
    conseil:    { libelle: 'Conseil de département', icone: 'fa-landmark',          couleur: '#9f1239', module: 'vie-departement' },
    jury:       { libelle: 'Jury',                 icone: 'fa-scale-balanced',      couleur: '#EF4444', module: 'jury' },
    stage:      { libelle: 'Stage & alternance',   icone: 'fa-building',            couleur: '#16a34a', module: 'stage' },
    voeux:      { libelle: 'Vœux & enquêtes',      icone: 'fa-square-poll-vertical', couleur: '#0d9488', module: 'voeux' },
    poursuite:  { libelle: "Poursuite d'études",   icone: 'fa-graduation-cap',      couleur: '#65a30d', module: 'poursuite' },
    communication: { libelle: 'Communication',     icone: 'fa-envelope',            couleur: '#c026d3', module: 'communication' },
    rentree:    { libelle: 'Rentrée / Fin',        icone: 'fa-flag',                couleur: '#10B981', module: '' },
    evenement:  { libelle: 'Vie étudiante',        icone: 'fa-star',                couleur: '#EC4899', module: '' },
    examen:     { libelle: 'Examen',               icone: 'fa-pen',                 couleur: '#8B5CF6', module: '' },
    reunion:    { libelle: 'Réunion',              icone: 'fa-users',               couleur: '#6B7280', module: 'enseignants' }
};

/**
 * Les règles, dans l'ordre d'évaluation.
 *   `motif`    expression cherchée dans le libellé (minuscules, accents retirés)
 *   `categorie` code de `CATEGORIES_PROPOSABLES`
 *   `pourquoi`  phrase affichée à l'utilisateur — une proposition doit se justifier
 */
export const REGLES_CLASSEMENT = [
    {
        motif: /soutenance/,
        categorie: 'soutenance',
        pourquoi: 'Soutenance'
    },
    {
        // ⚠️ AVANT « jury », et ce n'est pas un détail : le jeu réel contient
        // « CONSEIL DEPARTEMENT FEVRIER VS JURY », qui porte les deux mots. Un
        // conseil reste un conseil même s'il se tient autour d'un jury — et
        // « conseil » est le signal le plus spécifique des deux. Faute attrapée en
        // passant les 33 libellés de production dans les règles, pas à la relecture.
        motif: /conseil/,
        categorie: 'conseil',
        pourquoi: 'Conseil de département'
    },
    {
        motif: /jury/,
        categorie: 'jury',
        pourquoi: 'Jury'
    },
    {
        // Avant la règle générique « réunion », et avant « stage » : un RIMA est une
        // réunion AVEC des entreprises, que le module Stages a vocation à préparer.
        motif: /\brima\b|maitre.{0,4}d.?apprentissage/,
        categorie: 'stage',
        pourquoi: "Réunion des maîtres d'apprentissage"
    },
    {
        motif: /voeux|enquete|satisfaction/,
        categorie: 'voeux',
        pourquoi: 'Vœux et enquêtes'
    },
    {
        motif: /vacataire/,
        categorie: 'reunion',
        pourquoi: 'Réunion de vacataires'
    },
    {
        motif: /stage|alternan|apprenti|entreprise|tuteur|forum/,
        categorie: 'stage',
        pourquoi: 'Stage / alternance'
    },
    {
        motif: /poursuite|parcoursup|salon de l.?etudiant/,
        categorie: 'poursuite',
        pourquoi: "Poursuite d'études"
    },
    {
        motif: /portes ouv|salon|parents|presse/,
        categorie: 'communication',
        pourquoi: 'Événement public'
    },
    {
        // Repères de calendrier : la catégorie « Rentrée / Fin » n'a VOLONTAIREMENT
        // pas de module. Personne n'a de tâche à cocher sur une « Fin S2 » ; la
        // rattacher remplirait l'Accueil d'étapes sans action.
        motif: /rentree|fin de formation|^fin s\d|fin s\d| fin s\d/,
        categorie: 'rentree',
        pourquoi: 'Repère de calendrier — aucune tâche à suivre'
    },
    {
        motif: /integration|hackathon|seminaire|gala|bde/,
        categorie: 'evenement',
        pourquoi: 'Vie étudiante'
    },
    {
        motif: /examen|\bds\b|rattrapage|ratrapage|controle/,
        categorie: 'examen',
        pourquoi: 'Évaluation'
    },
    {
        motif: /reunion/,
        categorie: 'reunion',
        pourquoi: 'Réunion'
    }
];

/**
 * Minuscules, accents retirés : « Rentrée » et « RENTREE » doivent se valoir.
 *
 * ⚠️ LES LIGATURES SE TRAITENT À PART. `normalize('NFD')` décompose les lettres
 * accentuées, mais PAS « œ » ni « æ », qui sont des caractères à part entière et
 * non des lettres à diacritique. Sans les deux remplacements ci-dessous, « Fin
 * Vœux Saé S2 » n'était reconnu par aucune règle — constaté en passant les 33
 * libellés réels de production dans le moteur. Un mot français sur lequel butent
 * les expressions régulières, c'est exactement le genre de silence qui aurait fait
 * passer cet événement pour « à classer à la main » sans qu'on sache pourquoi.
 */
function normaliser(texte) {
    return String(texte || '')
        .toLowerCase()
        .replace(/œ/g, 'oe')
        .replace(/æ/g, 'ae')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}

/** Première règle applicable à un libellé, ou `null`. */
export function regleApplicable(libelle) {
    const t = normaliser(libelle);
    return REGLES_CLASSEMENT.find(r => r.motif.test(t)) || null;
}

/**
 * Proposition de classement pour une liste d'événements, sachant les catégories
 * qui existent déjà dans le département.
 *
 * Renvoie deux listes :
 *   `categoriesACreer` — les catégories réclamées par au moins un événement et
 *                        absentes du référentiel, avec le nombre d'événements qui
 *                        les demandent ;
 *   `lignes`           — une ligne par événement, TOUJOURS, y compris pour ceux
 *                        qu'aucune règle ne couvre. Cacher les cas non résolus
 *                        donnerait l'illusion d'un classement complet ; les montrer
 *                        dit où reste le travail.
 *
 * `retenu` = pré-coché. Seuls les événements ENCORE EN « AUTRE » le sont : la
 * proposition ne remet jamais en cause un classement fait à la main.
 */
export function proposerClassement(evenements, categories) {
    const existants = new Set((categories || []).map(c => c.code));
    const reclamees = new Map();

    const lignes = (evenements || []).map(evt => {
        const regle = regleApplicable(evt.libelle);
        const actuelle = evt.categorie || CODE_FILET;

        if (!regle) {
            return {
                evenement: evt, regle: null, retenu: false,
                categorieActuelle: actuelle, categorieProposee: null,
                aCreer: false, dejaClasse: actuelle !== CODE_FILET,
                pourquoi: 'Aucune règle ne reconnaît ce libellé — à classer à la main'
            };
        }

        const cible = regle.categorie;
        const aCreer = !existants.has(cible);
        if (aCreer) reclamees.set(cible, (reclamees.get(cible) || 0) + 1);

        const dejaClasse = actuelle !== CODE_FILET;
        const changerait = actuelle !== cible;

        return {
            evenement: evt, regle,
            categorieActuelle: actuelle,
            categorieProposee: cible,
            aCreer,
            dejaClasse,
            // Pré-coché seulement si l'événement est encore dans le filet ET que la
            // proposition l'en sort. Un événement déjà rangé ailleurs est montré,
            // décoché : c'est peut-être un choix, et une proposition ne défait pas
            // un choix sans qu'on le lui demande.
            retenu: changerait && !dejaClasse,
            pourquoi: regle.pourquoi
        };
    });

    const categoriesACreer = [...reclamees.entries()].map(([code, nb]) => ({
        code,
        ...CATEGORIES_PROPOSABLES[code],
        nb_evenements: nb,
        // Créer une catégorie que rien ne réclame serait du bruit ; celles-ci sont
        // toutes réclamées, donc toutes pré-cochées.
        retenu: true
    }));

    return { categoriesACreer, lignes };
}

/**
 * Libellé lisible d'un code de catégorie, que celle-ci existe déjà ou qu'elle
 * soit encore à créer. Sans ce repli, la modale afficherait le code technique
 * d'une catégorie qu'elle propose justement de créer.
 */
export function libelleProposition(code, categories) {
    if (!code) return '—';
    const existante = (categories || []).find(c => c.code === code);
    if (existante) return existante.libelle;
    return CATEGORIES_PROPOSABLES[code]?.libelle || code;
}

/** Apparence à afficher pour un code proposé (catégorie existante ou à créer). */
export function apparenceProposition(code, categories) {
    const existante = (categories || []).find(c => c.code === code);
    if (existante) return { icone: existante.icone, couleur: existante.couleur };
    const aCreer = CATEGORIES_PROPOSABLES[code];
    if (aCreer) return { icone: aCreer.icone, couleur: aCreer.couleur };
    const filet = categorieDe(CODE_FILET, categories);
    return { icone: filet.icone, couleur: filet.couleur };
}

/**
 * Contrôle de cohérence, appelé par l'auto-contrôle : toute règle doit viser une
 * catégorie que le fichier sait décrire, sinon « Proposer un classement » offrirait
 * de créer une catégorie sans libellé ni symbole.
 */
export function reglesInvalides() {
    return REGLES_CLASSEMENT
        .filter(r => !CATEGORIES_PROPOSABLES[r.categorie])
        .map(r => r.categorie);
}

/** Le module que la catégorie proposée apporterait, pour l'expliquer à l'écran. */
export function moduleDeLaProposition(code) {
    const cible = CATEGORIES_PROPOSABLES[code];
    return cible?.module ? moduleEvenement(cible.module) : null;
}
